import { PropsWithChildren } from 'react'
import { useAppliedBaseFilters } from 'hooks/useAppliedBaseFilters'
import { feedback, feedbackV2 } from '@ecosystem/newron-design-system'
import { Cell } from 'reducers/subscription/user'
import { isEmpty } from 'lodash'

export interface RouteRestrictionProps {
  codes?: ITEM_GROUP[]
  restrictionType: RESTRICTION_TYPE
  notInCodes?: ITEM_GROUP[]
  pageRestriction?: boolean
  countryCodes?: COUNTRIES[]
}
export enum RESTRICTION_TYPE {
  ITEM_GROUP = 'itemGroup',
  COUNTRY = 'country',
  WAVE = 'wave',
  SUB_GROUP = 'subGroup',
}
export enum COUNTRIES {
  GERMANY = 'DE',
  FRANCE = 'FR',
  GREAT_BRITAIN = 'UK',
  ITALY = 'IT',
  SPAIN = 'ES',
  BELGIUM = 'BE',
  NETHERLANDS = 'NL',
  POLAND = 'PL',
  AUSTRIA = 'AT',
  SWITZERLAND = 'CH',
}
export enum ITEM_GROUP {
  HOT_BEVERAGE_MAKER = 'HotBeverageMaker',
  PRINTER = 'Printer',
  MOBILE_PHONE = 'MobilePhone',
  TV = 'TV',
  LAPTOP = 'Laptop',
  HEADPHONES = 'Headphones',
  HOMEASSISTANT = 'HomeAssistant',
  FR = 'FR',
  DE = 'DE',
  UK = 'UK',
  RU = 'RU',
  IN = 'IN',
  IT = 'IT',
  ES = 'ES',
  TABLET = 'Tablet',
  VACUUM_CLEANER = 'VacuumCleaner',
  DIGITAL_CAMERAS = 'DigitalCameraCamcorder',
  DENTAL_CARE = 'dentalcare',
  TUMBLE_DRYER = 'TumbleDryer',
  COOKER_HOB = 'CookerHob',
  REFRIGERATOR = 'RefrigeratorsFreezers',
  DISHWASHER = 'Dishwasher',
  WASHING_MACHINE = 'WashingMachine',
  GAMING_CONSOLE = 'GamingConsole',
  HAIR_CARE = 'haircare',
  CORE_WEARABLES = 'smartwatch',
  SHAVERS = 'Shaver',
  FOOD_PREP = 'foodprep',
}

export const hasNotValidAppliedFilters = (
  ids: string[],
  restrictionType: RESTRICTION_TYPE,
  marketFilters?: string[],
  productFilters?: string[]
): boolean => {
  switch (restrictionType) {
    case RESTRICTION_TYPE.ITEM_GROUP: {
      const productAppliedFilters = productFilters?.filter((product) => !ids.includes(product))
      return !productAppliedFilters?.length
    }
    case RESTRICTION_TYPE.COUNTRY: {
      const contryRestriction = marketFilters?.filter((market) => !ids.includes(market))
      return !contryRestriction?.length
    }
    default:
      return false
  }
}
export const hasValidSubcription = (
  ids: string[] | undefined,
  subscriptions: Cell[] | null,
  restrictionType: RESTRICTION_TYPE
): boolean => {
  if (!subscriptions) return false

  if (isEmpty(subscriptions)) return true
  switch (restrictionType) {
    case RESTRICTION_TYPE.ITEM_GROUP:
      return !!subscriptions.filter((sub) => ids?.includes(sub.itemGroup)).length
    case RESTRICTION_TYPE.COUNTRY:
      return !!subscriptions.filter((sub) => ids?.includes(sub.country)).length
    case RESTRICTION_TYPE.WAVE:
      return !!subscriptions.flatMap((sub) => sub.waves.filter((wave) => ids?.includes(wave)))
        .length
    case RESTRICTION_TYPE.SUB_GROUP:
      return !!subscriptions.filter((sub) => sub.subGroup).length

    default:
      return false
  }
}
export const hasValidAppliedFilters = (
  ids: string[] | undefined,
  restrictionType: RESTRICTION_TYPE,
  marketFilters?: string[],
  productFilters?: string[],
  subGroups?: boolean[],
  countryCodes?: string[]
): boolean => {
  switch (restrictionType) {
    case RESTRICTION_TYPE.ITEM_GROUP:
      return !!productFilters?.filter((product) => ids?.includes(product)).length
    case RESTRICTION_TYPE.COUNTRY:
      return !!marketFilters?.filter((market) => ids?.includes(market)).length
    case RESTRICTION_TYPE.SUB_GROUP:
      return (
        (subGroups === null || isEmpty(subGroups) || !!subGroups?.filter((item) => item)?.length) &&
        !!productFilters?.filter((product) => ids?.includes(product)).length &&
        !!marketFilters?.filter((market) => countryCodes?.includes(market)).length
      )

    default:
      return false
  }
}

export const RouteRestriction = ({
  codes,
  restrictionType,
  children,
  notInCodes,
  pageRestriction,
  countryCodes,
}: PropsWithChildren<RouteRestrictionProps>) => {
  const { Alert: InlineAlert } = feedbackV2

  const { countryFilters, productFilters, subGroupFiltersBase } = useAppliedBaseFilters()
  const countries = countryFilters?.map((item) => item?.categoryName)
  const itemGroups = productFilters?.map((item) => item?.categoryName)
  const subGroups = subGroupFiltersBase
    ?.filter((item) => countries.includes(item.country) && itemGroups.includes(item.itemGroup))
    ?.map((item) => item.subGroup)
  const countryError = !!countries?.filter((market) => countryCodes?.includes(market as COUNTRIES))
    .length
  if (notInCodes?.length) {
    return hasNotValidAppliedFilters(
      notInCodes,
      restrictionType,
      countries as string[],
      itemGroups as string[]
    ) ? (
      <></>
    ) : (
      <>{children} </>
    )
  } else if (
    hasValidAppliedFilters(
      codes,
      restrictionType,
      countries as string[],
      itemGroups as string[],
      subGroups as boolean[],
      countryCodes
    )
  ) {
    return <>{children}</>
  } else if (pageRestriction) {
    if (restrictionType === RESTRICTION_TYPE.SUB_GROUP) {
      const errorMessage = !countryError ? 'Country' : 'Product Group'
      return (
        <InlineAlert
          title={`Change the ${errorMessage} filter to see data on this page`}
          type="info"
          size={feedback.AlertSize.SMALL}
        />
      )
    } else {
      return (
        <InlineAlert
          title={'Change the Product Group filter to see data on this page'}
          type="info"
          size={feedback.AlertSize.SMALL}
        />
      )
    }
  } else {
    return <></>
  }
}
